import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Ship,
    Package,
    ArrowRight,
    TrendingUp,
    AlertCircle,
    CheckCircle,
    XCircle,
    Sparkles,
    Brain,
    ArrowUpRight,
    ArrowDownRight,
    Truck,
    Globe,
    Building2,
    List,
    LayoutGrid,
    Plus,
    BookmarkPlus,
    Activity,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { toast } from "sonner";

import {
    getBookingList,
    deletebookingDetails,
    getBookingDetails,
    getCancelBookingList,
    reinstateBookingList,
} from "@/redux/Booking/actions";
import { callLoader } from "@/redux/Loader/actions";
import Modal from "@/components/ui/Modal";
import FileUpload from "@/components/FileUpload/FileUpload";
import UploadNotes from "@/components/NotesUpload/NotesUpload";
import FilterBar from "@/components/shipment/FilterBar";
import ExpandedDetails from "@/components/booking/ExpandedDetails";
import ShipmentCard from "@/components/shipment/ShipmentCard";
import Pagination from "@/pages/PortPlace/Pagination";
import BookingCancelRestoreModal from "@/components/Modal/BookingCancelRestoreModal";
import { showToast } from "@/redux/Toast/actions";
import AIInsightCard from "@/components/shipment/AIInsightCard";
import AIInsightsModal from "@/components/shipment/AIInsightsModal";

// ─── Animated typewriter title ─────────────────────────────────────────────────
const TITLE = "Canceled Shipment Management";
function AnimatedTitle() {
    return (
        <motion.h1
            initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight
                       text-gray-900 dark:text-white"
        >
            <motion.span
                className="inline-block overflow-hidden whitespace-nowrap align-bottom"
                initial={{ width: "0ch" }}
                animate={{ width: ["0ch", `${TITLE.length}ch`, `${TITLE.length}ch`, "0ch"] }}
                transition={{
                    duration: 4.8,
                    times: [0, 0.22, 0.9, 1],
                    ease: ["linear", "linear", "easeInOut"],
                    repeat: Infinity,
                }}
            >
                {TITLE}
            </motion.span>
        </motion.h1>
    );
}

// ─── View Toggle ───────────────────────────────────────────────────────────────
function ViewToggle({ view, setView }) {
    const base =
        "w-9 h-9 flex items-center justify-center rounded-md transition-colors";
    const active =
        "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400";
    const inactive =
        "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100";

    return (
        <div
            className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700
                       bg-white dark:bg-gray-900 p-0.5"
        >
            <button
                onClick={() => setView("table")}
                title="List view"
                className={`${base} ${view === "table" ? active : inactive}`}
            >
                <List className="w-4 h-4" />
            </button>
            <button
                onClick={() => setView("card")}
                title="Grid view"
                className={`${base} ${view === "card" ? active : inactive}`}
            >
                <LayoutGrid className="w-4 h-4" />
            </button>
        </div>
    );
}

// ─── Main Component ────────────────────────────────────────────────────────────
const CancelShipmentList = ({ onCreateNew, onViewShipment, onPrintShipment }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // ---------- REDUX: Booking state ----------
    const { cancelBookingList, cancelPagination, cancelSummary } =
        useSelector((state) => state.booking);
    const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
    const userName = authUser?.username;
    const companyId = authUser?.companyId;

    const [showNote, setshowNote] = useState(false);
    const [fileUploadVisible, setFileUploadVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [charges, setCharges] = useState([]);

    const [payload, setPayload] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [showAIInsights, setShowAIInsights] = useState(false);
    const [expand, setExpand] = useState("");
    const [expandedRow, setExpandedRow] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [view, setView] = useState("table");
    const [left, setLeft] = useState(0);

    const [restoreData, setRestoreData] = useState(null);

    const divRef = useRef(null);
    const [width, setWidth] = useState(0);

    // Fetch shipments from API
    const fetchBookings = async (page = currentPage, filter = null) => {
        dispatch(callLoader(1));
        setIsLoading(true);
        try {
            const payload = {
                IPageNo: page,
                IPageRecords: filter?.pageSize || recordsPerPage,
                FilterType: filter?.status || "ALL",
                SearchKeyword: filter?.search || "",
                CARRIER: filter?.carrier || "",
                pol: filter?.pol || "",
                pod: filter?.pod || "",
                shortby: filter?.sortBy || "",
                FromDate: filter?.dateFrom || "",
                ToDate: filter?.dateTo || "",
                compnayId: companyId || 0,
            };
            await dispatch(getCancelBookingList(payload));
            setCurrentPage(page);
        } catch (error) {
            console.error("Error fetching cancel bookings:", error);
            toast.error("Failed to load cancel shipments");
        } finally {
            dispatch(callLoader(0));
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings(1);
    }, []);

    useEffect(() => {
        const update = () => divRef.current && setWidth(divRef.current.offsetWidth);
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [divRef, cancelBookingList]);

    const handleFilter = (filter) => {
        setPayload(filter);
        fetchBookings(1, filter);
    };

    const handlePageSize = (size) => {
        setRecordsPerPage(size);
        fetchBookings(1, { pageSize: size });
        setCurrentPage(1);
    };

    // Get unique carriers for filter
    const uniqueCarriers = [
        ...new Set(cancelBookingList.map((s) => s.carrier).filter(Boolean)),
    ];

    async function _getBooking(id) {
        try {
            const resultData = await dispatch(getBookingDetails(id,companyId));
            setCharges(resultData?.rates ?? []);
        } catch (error) {
            toast.error("Failed to load booking details", error);
        }
    }

    const handlePrint = (shipment) => {
        window.open(`/booking/print/${btoa(shipment?.id)}`, "_blank");
    };

    const handleEmail = (shipment) => {
        navigate(`/mail/compose?id=${shipment?.id || shipment?.BookingId || shipment?.BolID}&type=Booking`);
    };

    const changePage = (page) => {
        if (currentPage === page) return;
        setCurrentPage(page);
        fetchBookings(page, payload);
    };

    const handleOpenNotes = (shipment) => {
        setSelectedOrderId(shipment);
        setshowNote(true);
    };

    const handleOpenFileUpload = (shipment) => {
        setSelectedOrderId(shipment);
        setFileUploadVisible(true);
    };

    const _setRestoreDataPopup = (data) => {
        setRestoreData(data);
    };

    const _setRestoreData = async (remark) => {
        const payload = {
            BookingId: restoreData?.id || 0,
            ModifyBy: userName,
            BookingReinstateRemarks: remark,
        };
        await reinstateBookingList(payload)
            .then(() => {
                fetchBookings(currentPage);
            })
            .catch((error) => {
                dispatch(showToast("error", error?.message || "Failed to restore shipment"));
                console.error("Error restoring:", error);
            })
            .finally(() => {
                setRestoreData(null);
            });
    };

    const insightCards = useMemo(
        () => [
            {
                icon: Package,
                title: "Total Canceled",
                value: cancelSummary?.total || 0,
                trend: "+12% from last month",
                trendUp: true,
                accentKey: "blue",
            },
            {
                icon: Activity,
                title: "Pending Review",
                value: cancelSummary?.active || 0,
                trend: "On track",
                trendUp: true,
                accentKey: "emerald",
            },
            {
                icon: AlertCircle,
                title: "Needs Attention",
                value: cancelSummary?.needattension || 0,
                trend: "Review required",
                trendUp: false,
                accentKey: "red",
            },
        ],
        [cancelSummary],
    );

    const toggleExpandRow = (id) => {
        if (expandedRow === id) {
            setExpandedRow(null);
        } else {
            setExpandedRow(id);
            _getBooking(id);
        }
    };

    // ── Shared card props ─────────────────────────────────────────────────────
    const cardProps = (shipment, index) => ({
        key: shipment.id || index,
        shipment,
        onView: onViewShipment,
        onPrint: handlePrint,
        onEmail: handleEmail,
        onDelete: _setRestoreDataPopup,
        onOpenNotes: handleOpenNotes,
        onOpenFileUpload: handleOpenFileUpload,
        expand,
        setExpand,
        isDeleted: true,
        setRestoreDeletePopup: _setRestoreDataPopup,
    });

    // ──────────────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-full space-y-3">

            {/* ════════════════════════════════════════════════════
                HEADER CARD
            ════════════════════════════════════════════════════ */}
            <div className="rounded-2xl overflow-hidden
                            border border-gray-200 dark:border-gray-800
                            bg-white dark:bg-gray-900
                            shadow-sm dark:shadow-gray-900/40">

                {/* ── Top bar ───────────────────────────────────── */}
                <div className="px-4 sm:px-5 py-4
                                flex flex-wrap items-center justify-between gap-3
                                border-b border-gray-100 dark:border-gray-800">

                    {/* Left — icon + title + desc */}
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl
                                        bg-blue-50 dark:bg-blue-900/40
                                        ring-1 ring-blue-100 dark:ring-blue-800
                                        flex items-center justify-center">
                            <Ship className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.2} />
                        </div>
                        <div className="min-w-0">
                            <AnimatedTitle />
                            <p className="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400
                                          hidden sm:block truncate max-w-sm lg:max-w-lg">
                                Track, manage, and restore your canceled shipments with AI-powered insights.
                            </p>
                        </div>
                    </div>

                    {/* Right — actions */}
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        {/* Rate Quote */}
                        <button
                            onClick={() => navigate("/rate-quote")}
                            className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                                       bg-white dark:bg-gray-800
                                       border border-gray-200 dark:border-gray-700
                                       text-gray-700 dark:text-gray-300
                                       hover:bg-gray-50 hover:border-gray-300
                                       dark:hover:bg-gray-700/70 dark:hover:border-gray-600
                                       text-[12.5px] font-semibold transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            <span className="hidden xs:inline">Rate Quote</span>
                            <span className="xs:hidden">Quote</span>
                        </button>

                        {/* New Booking */}
                        <button
                            onClick={onCreateNew}
                            className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                                       bg-blue-600 hover:bg-blue-700 active:bg-blue-800
                                       dark:bg-blue-500 dark:hover:bg-blue-600
                                       text-white text-[12.5px] font-semibold
                                       shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)]
                                       dark:shadow-[0_4px_14px_-4px_rgba(59,130,246,0.35)]
                                       transition-colors"
                        >
                            <BookmarkPlus className="w-3.5 h-3.5" />
                            <span className="hidden xs:inline">New Booking</span>
                            <span className="xs:hidden">Booking</span>
                        </button>

                        {/* View toggle */}
                        <ViewToggle view={view} setView={setView} />
                    </div>
                </div>

                {/* ── KPI Cards ──────────────────────────────────── */}
                <div className="px-4 sm:px-5 pt-4 pb-3
                                grid grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3">
                    {insightCards.map((card) => (
                        <AIInsightCard key={card.title} {...card} />
                    ))}
                </div>

                {/* ── AI Insight Banner ─────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="mx-4 sm:mx-5 mb-4 relative overflow-hidden
                               flex flex-col sm:flex-row items-start sm:items-center gap-3
                               rounded-xl px-4 py-3
                               bg-gradient-to-r from-violet-50 to-indigo-50
                               dark:from-violet-950/50 dark:to-indigo-950/50
                               border border-violet-100 dark:border-violet-900/60"
                >
                    {/* Left accent stripe */}
                    <span
                        aria-hidden
                        className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl
                                   bg-gradient-to-b from-violet-500 via-fuchsia-500 to-pink-500"
                    />

                    {/* Sparkle icon */}
                    <div className="shrink-0 w-9 h-9 rounded-lg ml-1
                                    bg-gradient-to-br from-violet-500 to-indigo-500
                                    flex items-center justify-center
                                    shadow-[0_4px_12px_-2px_rgba(139,92,246,0.4)]">
                        <Sparkles className="w-4 h-4 text-white" />
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em]
                                        text-violet-600 dark:text-violet-400">
                            AI Insight
                        </div>
                        <p className="mt-0.5 text-[12.5px] leading-relaxed
                                      text-gray-700 dark:text-gray-300">
                            Based on current trends,{" "}
                            <span className="font-semibold text-gray-900 dark:text-white">5 canceled shipments</span>{" "}
                            can be restored. Consider reviewing{" "}
                            <span className="font-semibold text-gray-900 dark:text-white">
                                2 high-priority shipments
                            </span>{" "}
                            from MAERSK carrier.
                        </p>
                    </div>

                    {/* CTA */}
                    <button
                        onClick={() => setShowAIInsights(true)}
                        className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg
                                   text-[12px] font-semibold
                                   text-violet-700 dark:text-violet-300
                                   bg-violet-100 dark:bg-violet-900/50
                                   hover:bg-violet-200 dark:hover:bg-violet-900
                                   ring-1 ring-violet-200 dark:ring-violet-800
                                   transition-colors"
                        data-testid="view-ai-insights-btn"
                    >
                        View Details <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </motion.div>
            </div>
            {/* ════ END HEADER CARD ════ */}

            {/* ── Filter Bar ──────────────────────────────────────── */}
            <FilterBar
                uniqueCarriers={uniqueCarriers}
                onFilter={handleFilter}
                stats={{
                    total: cancelPagination?.totalRecord,
                    showRecord: cancelPagination?.currentRecord || 0,
                }}
            />

            {/* ── Content ─────────────────────────────────────────── */}
            <div className="p-1">
                {cancelBookingList.length === 0 ? (
                    /* Empty state */
                    <div className="text-center py-20
                                    bg-white dark:bg-gray-900
                                    rounded-2xl border border-gray-200 dark:border-gray-800">
                        <Ship className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-300 font-semibold text-lg">
                            No canceled shipments found
                        </p>
                        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                            No canceled bookings to display
                        </p>
                    </div>
                ) : view !== "table" ? (
                    /* Card grid */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                        {cancelBookingList.map((shipment, i) => (
                            <ShipmentCard {...cardProps(shipment, i)} />
                        ))}
                    </div>
                ) : (
                    /* List view */
                    <div className="space-y-4 overflow-auto rounded-xl mt-4" ref={divRef}>
                        {cancelBookingList.map((shipment, i) => (
                            <ShipmentCard {...cardProps(shipment, i)} />
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {!isLoading && cancelPagination?.totalPage > 0 && (
                    <Pagination
                        className="border-t border-gray-200 dark:border-gray-800
                                   pl-2 pr-16 pt-4 pb-8 mb-4 mt-8
                                   bg-gray-50 dark:bg-gray-800/50"
                        currentPage={currentPage}
                        recordsPerPage={recordsPerPage}
                        setRecordsPerPage={handlePageSize}
                        onPrevious={changePage}
                        onNext={changePage}
                        totalRecord={cancelPagination?.totalRecord}
                        handleGoto={changePage}
                        title="shipments"
                    />
                )}
            </div>

            {/* ── Modals ──────────────────────────────────────────── */}
            <AnimatePresence>
                {showAIInsights && (
                    <AIInsightsModal
                        isOpen={showAIInsights}
                        onClose={() => setShowAIInsights(false)}
                        shipments={cancelBookingList}
                        insightCards={insightCards}
                    />
                )}
            </AnimatePresence>

            <Modal isOpen={fileUploadVisible} onClose={() => { setFileUploadVisible(false); setSelectedOrderId(null); }}>
                <FileUpload orderId={selectedOrderId?.id} orderNum={selectedOrderId?.fileNumber} type="booking" />
            </Modal>

            <Modal isOpen={showNote} onClose={() => { setshowNote(false); setSelectedOrderId(null); }}>
                <UploadNotes BookingID={selectedOrderId?.id || ""} RefNumber={selectedOrderId?.fileNumber} type="Booking" />
            </Modal>

            {restoreData && (
                <BookingCancelRestoreModal
                    open={restoreData ? true : false}
                    onClose={() => setRestoreData(null)}
                    onDelete={(data) => {
                        _setRestoreData(data);
                    }}
                    title="Restore Shipment"
                    description="Provide a note before restoring this shipment."
                    confirmLabel="Restore"
                    cancelLabel="Cancel"
                    variant="warning"
                />
            )}
        </div>
    );
};

export default CancelShipmentList;